import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import AccommodationRoomDetailsList from '../AccommodationRoomDetailsList'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { isHotelPackage } from 'lib/offer/offerTypes'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import AccommodationAmenitiesAndPoliciesModal from '../AccommodationAmenitiesAndPoliciesModal'
import TextButton from 'LuxKit/Button/TextButton'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import BodyText from 'LuxKit/Typography/BodyText'

interface Details {
  name: string
  description?: string
}

function getHotelPackageDetails(roomPackage: App.HotelPackage): Details {
  return {
    name: roomPackage.roomType.group?.name ?? roomPackage.roomType.name,
    description: roomPackage.roomType.description,
  }
}

function getBedbankPackageDetails(roomPackage: App.BedbankPackage): Details {
  return {
    name: roomPackage.name,
  }
}

export const AccommodationIndividualSelectRoomTileDetailsContainer = styled(Group)`
  padding: ${rem(20)};
  min-width: 0;
`
interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  actions?: ReactNode
}

function AccommodationIndividualSelectRoomTileDetails(props: Props) {
  const {
    actions,
    roomPackage,
    urgencyLabels,
  } = props

  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const openAmenitiesAndPoliciesModal = useCallback(() => {
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} />)
  }, [roomPackage, showModal])

  const {
    name,
    description,
  } = isHotelPackage(roomPackage) ?
      getHotelPackageDetails(roomPackage) :
      getBedbankPackageDetails(roomPackage)

  const readLessText = intl.formatMessage({
    id: 'xoIkTP9H8X',
    defaultMessage: 'Read less',
    description: 'Label for collapsing expandable room description text.',
  })

  const readMoreText = intl.formatMessage({
    id: 'vSaopAzgwO',
    defaultMessage: 'Read more',
    description: 'Label for expanding room description text.',
  })

  const viewAmenitiesAndPoliciesText = intl.formatMessage({
    id: 'QbR+KgQfI2',
    defaultMessage: 'View amenities & policies',
    description: 'CTA button text to open the amenities and policies modal from the room tile details.',
  })

  return <AccommodationIndividualSelectRoomTileDetailsContainer direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Heading variant="heading4">{name}</Heading>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
      {description && <LargeContentToggle
        maxHeight={80}
        collapsedHeight={70}
        showLessText={readLessText}
        showMoreText={readMoreText}
      >
        <BodyText variant="medium">{description}</BodyText>
      </LargeContentToggle>}
      {urgencyLabels && <LabelGroup>{urgencyLabels}</LabelGroup>}
    </VerticalSpacer>
    <Clickable onClick={openAmenitiesAndPoliciesModal}>
      <AccommodationRoomDetailsList roomPackage={roomPackage} />
    </Clickable>
    <div>
      <TextButton kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
        {viewAmenitiesAndPoliciesText}
      </TextButton>
    </div>
  </AccommodationIndividualSelectRoomTileDetailsContainer>
}

export default AccommodationIndividualSelectRoomTileDetails
